import { createSelector } from '@ngrx/store';
import { selectDigitaixState } from '../core.selectors';
import { splitControlIdInRootAndPath } from './helpers';
import {
  AbstractControlState,
  unbox
} from 'ngrx-forms';
import { DxFormState } from './form-state';

export const selectFormsState = createSelector(
  selectDigitaixState,
  state => state && state.forms
);

export const getForm = <Form, RawForm>(formId: string) =>
  createSelector(
    selectFormsState,
    state => (state && (state[formId] as DxFormState<Form, RawForm>)) || null
  );

export const getFormState = <
  T,
  F extends AbstractControlState<T> = AbstractControlState<T>
>(
  controlId: string
) =>
  createSelector(
    selectFormsState,
    state => {
      let formState: any = null;
      if (state) {
        const [formId, path] = splitControlIdInRootAndPath(controlId);
        const dxFormState = state[formId] || null;
        if (dxFormState) {
          formState = dxFormState.form;
          if (formState && path) {
            for (const control of path.split('.')) {
              formState = formState.controls[control] || null;
              if (formState === null) {
                break;
              }
            }
          }
        }
      }
      return formState as F;
    }
  );

export const getFormValue = <Form>(controlId: string) =>
  createSelector(
    getFormState(controlId),
    formState => formState && (formState.value as Form)
  );

export const getFormUnboxedValue = <FormValue>(controlId: string) =>
  createSelector(
    getFormValue<FormValue>(controlId),
    value => unbox(value)
  );

export const getFormComponent = <Form, RawForm>(formId: string) =>
  createSelector(
    getForm<Form, RawForm>(formId),
    state => state && state.editComponent
  );

export const getFormLoadCollactionPaths = (formId: string) =>
  createSelector(
    getForm(formId),
    state => state.load
  );

export const getFormTitle = (formId: string) =>
  createSelector(
    getForm(formId),
    state => state.title || state.collectionPath
  );

export interface UnquieFieldItem {
  formId: string;
  path: string;
  controlId: string;
  collectionPath: string;
}

export const getFormsWithUnqieFields = createSelector(
  selectFormsState,
  state =>
    state
      ? Object.entries(state)
          .filter(
            ([formId, formState]) =>
              formState.unique.length && formState.collectionPath
          )
          .map(([formId, formState]) =>
            formState.unique.map(path => ({
              formId,
              path,
              controlId: [formId, path].join('.'),
              collectionPath: formState.collectionPath
            }))
          )
          .reduce((acc, v) => [...acc, ...v], [])
      : []
);
